import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tabs,
    Tab,
    Chip,
    Tooltip,
    IconButton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Email as EmailIcon, Delete as DeleteIcon, Refresh as RefreshIcon, LockReset as LockResetIcon, Security as SecurityIcon } from '@mui/icons-material';
import { apiService, UserAdminView, Tenant, Invitation } from '../../services/api';

const UserManagement: React.FC = () => {
    const [tab, setTab] = useState(0);

    // --- Users state ---
    const [users, setUsers] = useState<UserAdminView[]>([]);
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [tenantFilter, setTenantFilter] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState<string>('');

    // Edit/create user dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [editingUser, setEditingUser] = useState<UserAdminView | null>(null);
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'user',
        tenant_id: '',
        is_active: true
    });
    const [submitting, setSubmitting] = useState(false);

    // --- Invitations state ---
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [invitesLoading, setInvitesLoading] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);

    // Invite dialog
    const [openInviteDialog, setOpenInviteDialog] = useState(false);
    const [inviteForm, setInviteForm] = useState({ email: '', role: 'user', tenant_id: '' });
    const [inviteSubmitting, setInviteSubmitting] = useState(false);
    const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
    const [resendingToken, setResendingToken] = useState<string | null>(null);
    const [resetMfaDialogOpen, setResetMfaDialogOpen] = useState(false);
    const [userToResetMfa, setUserToResetMfa] = useState<UserAdminView | null>(null);
    const [resettingMfa, setResettingMfa] = useState(false);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const [usersData, tenantsData] = await Promise.all([
                apiService.getUsers(),
                apiService.getTenants()
            ]);
            setUsers(usersData);
            setTenants(tenantsData);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const fetchInvitations = async () => {
        try {
            setInvitesLoading(true);
            const data = await apiService.getInvitations();
            setInvitations(data);
            setInviteError(null);
        } catch (err: any) {
            setInviteError(err.response?.data?.detail || 'Failed to fetch invitations');
        } finally {
            setInvitesLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (tab === 1) fetchInvitations();
    }, [tab]);

    // ---- User CRUD ----

    const handleOpenDialog = (user?: UserAdminView) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                email: user.email,
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                password: '',
                role: user.role,
                tenant_id: user.tenant_id || '',
                is_active: user.is_active
            });
        } else {
            setEditingUser(null);
            setFormData({ email: '', first_name: '', last_name: '', password: '', role: 'user', tenant_id: '', is_active: true });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingUser(null);
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            if (editingUser) {
                await apiService.updateUser(editingUser.user_id, {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    password: formData.password || undefined,
                    role: formData.role,
                    is_active: formData.is_active,
                });
            } else {
                await apiService.createUser({
                    email: formData.email,
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    password: formData.password,
                    role: formData.role,
                    tenant_id: formData.tenant_id || 'default-tenant'
                });
            }
            handleCloseDialog();
            fetchUsers();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to save user');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResetMfa = async () => {
        if (!userToResetMfa) return;
        try {
            setResettingMfa(true);
            await apiService.resetUserMfa(userToResetMfa.user_id);
            setResetMfaDialogOpen(false);
            setUserToResetMfa(null);
            fetchUsers();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to reset MFA');
        } finally {
            setResettingMfa(false);
        }
    };

    const handleToggleActive = async (user: UserAdminView) => {
        try {
            await apiService.updateUser(user.user_id, { is_active: !user.is_active });
            fetchUsers();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to update user status');
        }
    };

    // ---- Invite ----

    const handleOpenInviteDialog = () => {
        setInviteForm({ email: '', role: 'user', tenant_id: '' });
        setInviteSuccess(null);
        setInviteError(null);
        setOpenInviteDialog(true);
    };

    const handleCloseInviteDialog = () => {
        setOpenInviteDialog(false);
        setInviteSuccess(null);
    };

    const handleSendInvite = async () => {
        if (!inviteForm.tenant_id) {
            setInviteError('Please select a tenant.');
            return;
        }
        try {
            setInviteSubmitting(true);
            await apiService.sendInvitation({
                email: inviteForm.email,
                role: inviteForm.role,
                tenant_id: inviteForm.tenant_id,
            });
            setInviteSuccess(`Invitation sent to ${inviteForm.email}`);
            if (tab === 1) fetchInvitations();
        } catch (err: any) {
            setInviteError(err.response?.data?.detail || 'Failed to send invitation');
        } finally {
            setInviteSubmitting(false);
        }
    };

    const handleRevokeInvitation = async (token: string) => {
        try {
            await apiService.revokeInvitation(token);
            fetchInvitations();
        } catch (err: any) {
            setInviteError(err.response?.data?.detail || 'Failed to revoke invitation');
        }
    };

    const handleResendInvitation = async (token: string) => {
        try {
            setResendingToken(token);
            await apiService.resendInvitation(token);
            fetchInvitations();
        } catch (err: any) {
            setInviteError(err.response?.data?.detail || 'Failed to resend invitation');
        } finally {
            setResendingToken(null);
        }
    };

    const filteredUsers = users.filter((user) => {
        const matchesTenant = tenantFilter === 'all' || user.tenant_id === tenantFilter;
        const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesTenant && matchesSearch;
    });

    const inviteStatus = (inv: Invitation): { label: string; color: 'success' | 'warning' | 'error' | 'default' } => {
        if (inv.used_at) return { label: 'Accepted', color: 'success' };
        if (new Date(inv.expires_at) < new Date()) return { label: 'Expired', color: 'error' };
        return { label: 'Pending', color: 'warning' };
    };

    if (loading && users.length === 0) return <CircularProgress />;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">User Management</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        onClick={handleOpenInviteDialog}
                    >
                        Invite User
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add User
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Users" />
                <Tab label="Invitations" />
            </Tabs>

            {/* ---- Users Tab ---- */}
            {tab === 0 && (
                <>
                    <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by Tenant</InputLabel>
                            <Select
                                value={tenantFilter}
                                label="Filter by Tenant"
                                onChange={(e) => setTenantFilter(e.target.value as string)}
                            >
                                <MenuItem value="all">All Tenants</MenuItem>
                                <MenuItem value=""><em>No Tenant</em></MenuItem>
                                {tenants.map(t => (
                                    <MenuItem key={t.tenant_id} value={t.tenant_id}>{t.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Search users..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </Paper>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Email</strong></TableCell>
                                    <TableCell><strong>Name</strong></TableCell>
                                    <TableCell><strong>Role</strong></TableCell>
                                    <TableCell><strong>Tenant</strong></TableCell>
                                    <TableCell><strong>MFA</strong></TableCell>
                                    <TableCell><strong>Active</strong></TableCell>
                                    <TableCell align="right"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.map((user) => (
                                    <TableRow key={user.user_id}>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '-'}</TableCell>
                                        <TableCell>{user.role}</TableCell>
                                        <TableCell>{tenants.find(t => t.tenant_id === user.tenant_id)?.name || 'None'}</TableCell>
                                        <TableCell>
                                            {user.mfa_enabled ? (
                                                <Tooltip title="MFA Enabled">
                                                    <Chip icon={<SecurityIcon sx={{ fontSize: '16px !important' }} />} label="Active" color="success" size="small" variant="outlined" />
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">Disabled</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={user.is_active}
                                                onChange={() => handleToggleActive(user)}
                                                color="primary"
                                                disabled={user.role === 'admin'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            {user.mfa_enabled && (
                                                <Tooltip title="Reset MFA">
                                                    <IconButton
                                                        color="warning"
                                                        onClick={() => {
                                                            setUserToResetMfa(user);
                                                            setResetMfaDialogOpen(true);
                                                        }}
                                                        size="small"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <LockResetIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Button startIcon={<EditIcon />} onClick={() => handleOpenDialog(user)}>
                                                Edit
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredUsers.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No users found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* ---- Invitations Tab ---- */}
            {tab === 1 && (
                <>
                    {inviteError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInviteError(null)}>{inviteError}</Alert>}
                    {invitesLoading ? (
                        <CircularProgress />
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Role</strong></TableCell>
                                        <TableCell><strong>Tenant</strong></TableCell>
                                        <TableCell><strong>Invited By</strong></TableCell>
                                        <TableCell><strong>Sent</strong></TableCell>
                                        <TableCell><strong>Expires</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell align="right"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invitations.map((inv) => {
                                        const { label, color } = inviteStatus(inv);
                                        const isPending = label === 'Pending';
                                        const isExpired = label === 'Expired';
                                        const canResend = isPending || isExpired;
                                        const isResending = resendingToken === inv.token;
                                        return (
                                            <TableRow key={inv.token}>
                                                <TableCell>{inv.email}</TableCell>
                                                <TableCell>{inv.role}</TableCell>
                                                <TableCell>{inv.tenant_name || '—'}</TableCell>
                                                <TableCell>{inv.invited_by_email || '—'}</TableCell>
                                                <TableCell>{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                                                <TableCell>{new Date(inv.expires_at).toLocaleDateString()}</TableCell>
                                                <TableCell>
                                                    <Chip label={label} color={color} size="small" />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {canResend && (
                                                        <Tooltip title="Resend invitation">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleResendInvitation(inv.token)}
                                                                disabled={isResending}
                                                            >
                                                                {isResending
                                                                    ? <CircularProgress size={16} />
                                                                    : <RefreshIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {isPending && (
                                                        <Tooltip title="Revoke invitation">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleRevokeInvitation(inv.token)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {invitations.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">No invitations sent yet.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            {/* ---- Create/Edit User Dialog ---- */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {!editingUser && (
                            <TextField
                                label="Email"
                                type="email"
                                fullWidth
                                required
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        )}
                        <TextField
                            label="First Name"
                            fullWidth
                            required
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        />
                        <TextField
                            label="Last Name"
                            fullWidth
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        />
                        <TextField
                            label={editingUser ? "Password (leave blank to keep current)" : "Password"}
                            type="password"
                            fullWidth
                            required={!editingUser}
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        />
                        {editingUser && (
                            <Typography variant="body2" color="text.secondary">
                                Editing settings for <strong>{editingUser.email}</strong>.
                            </Typography>
                        )}
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={formData.role}
                                label="Role"
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            >
                                <MenuItem value="user">User</MenuItem>
                                <MenuItem value="admin">System Admin</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth disabled={!!editingUser}>
                            <InputLabel>Tenant Assignment</InputLabel>
                            <Select
                                value={formData.tenant_id}
                                label="Tenant Assignment"
                                onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                                required
                            >
                                {tenants.map(t => (
                                    <MenuItem key={t.tenant_id} value={t.tenant_id}>{t.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>Active Account:</Typography>
                            <Switch
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={
                            submitting ||
                            (!editingUser && (!formData.email || !formData.password || !formData.tenant_id || !formData.first_name)) ||
                            (!!editingUser && !formData.first_name)
                        }
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ---- Invite User Dialog ---- */}
            <Dialog open={openInviteDialog} onClose={handleCloseInviteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Invite User</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {inviteSuccess ? (
                            <Alert severity="success">{inviteSuccess}</Alert>
                        ) : (
                            <>
                                {inviteError && (
                                    <Alert severity="error" onClose={() => setInviteError(null)}>{inviteError}</Alert>
                                )}
                                <TextField
                                    label="Email address"
                                    type="email"
                                    fullWidth
                                    required
                                    value={inviteForm.email}
                                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                                    disabled={inviteSubmitting}
                                    autoFocus
                                />
                                <FormControl fullWidth>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={inviteForm.role}
                                        label="Role"
                                        onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                                        disabled={inviteSubmitting}
                                    >
                                        <MenuItem value="user">User</MenuItem>
                                        <MenuItem value="admin">System Admin</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth required>
                                    <InputLabel>Tenant</InputLabel>
                                    <Select
                                        value={inviteForm.tenant_id}
                                        label="Tenant"
                                        onChange={(e) => setInviteForm({ ...inviteForm, tenant_id: e.target.value })}
                                        disabled={inviteSubmitting}
                                        error={!inviteForm.tenant_id}
                                    >
                                        {tenants.map(t => (
                                            <MenuItem key={t.tenant_id} value={t.tenant_id}>{t.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" color="text.secondary">
                                    The invitee will receive an email with a time-limited registration link.
                                </Typography>
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseInviteDialog}>
                        {inviteSuccess ? 'Close' : 'Cancel'}
                    </Button>
                    {!inviteSuccess && (
                        <Button
                            onClick={handleSendInvite}
                            variant="contained"
                            startIcon={inviteSubmitting ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
                            disabled={inviteSubmitting || !inviteForm.email || !inviteForm.tenant_id}
                        >
                            Send Invite
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* ---- Reset MFA Confirmation Dialog ---- */}
            <Dialog open={resetMfaDialogOpen} onClose={() => setResetMfaDialogOpen(false)}>
                <DialogTitle>Reset MFA</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to reset Multi-Factor Authentication for <strong>{userToResetMfa?.email}</strong>?
                    </Typography>
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        This will disable MFA immediately and allow the user to log in with just their password.
                        They will need to re-enroll in MFA to secure their account again.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetMfaDialogOpen(false)} disabled={resettingMfa}>Cancel</Button>
                    <Button
                        onClick={handleResetMfa}
                        color="error"
                        variant="contained"
                        disabled={resettingMfa}
                        startIcon={resettingMfa ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
                    >
                        Reset MFA
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagement;
 
